import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ESQUEMA_CLIENTES } from '@/lib/filtros-esquemas';

/** Los filtros que esta pantalla guarda en la URL. */
interface FiltrosClientes {
  search?: string;
  compro?: string;
  zona?: string;
  rango?: string;
}
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import * as clientesApi from '@/api/clientes.api';
import * as installationApi from '@/api/installation.api';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { jobStatusBadge, encuestaBadge } from '@/lib/badges';
import { formatPhoneNumber, dateTime } from '@/lib/format';
import { money } from '@/lib/money';
import { cn } from '@/lib/cn';
import { FiltrosGuardados } from '@/components/filtros/FiltrosGuardados';
import { CajaDeBusqueda } from '@/components/common/CajaDeBusqueda';
import { Cargando } from '@/components/common/Cargando';

/**
 * CLIENTES — la cartera del negocio: quién compró, qué, cuándo y por cuánto.
 * Solo lectura (la agregación vive en el API); el click abre la ficha completa
 * del cliente con su historial y el link a su conversación.
 */

type ComproFiltro = 'todos' | 'si' | 'no';

const COMPRO_CHIPS: { value: ComproFiltro; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'si', label: 'Compraron' },
  { value: 'no', label: 'No compraron' },
];

export default function Clientes() {
  // A1: los filtros viven en la URL. El booleano `esteMes` se reemplaza por el
  // rango unificado (?rango=mes): dice lo mismo y se lee igual que en las demas
  // pantallas, en vez de tener un parametro propio de esta.
  const { filtros, setFiltros } = useFiltrosUrl<FiltrosClientes>(ESQUEMA_CLIENTES);

  const search = filtros.search ?? '';
  const setSearch = (v: string) => setFiltros({ search: v || undefined });

  const compro = (filtros.compro ?? 'todos') as ComproFiltro;
  const setCompro = (v: ComproFiltro) => setFiltros({ compro: v === 'todos' ? undefined : v });

  const zona = filtros.zona ?? '';
  const setZona = (v: string) => setFiltros({ zona: v || undefined });

  const esteMes = filtros.rango === 'mes';
  const setEsteMes = (v: boolean) => setFiltros({ rango: v ? 'mes' : undefined });

  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
  });
  const currencyPrefix = crmView?.currencyPrefix || 'RD$';
  // Esta pantalla se arma sobre TRABAJOS: la ultima compra, el tecnico, el
  // estado y la encuesta salen de ahi. Un negocio que no instala nada no tiene
  // trabajos, asi que esas columnas no pueden llenarse nunca — y la fila queda
  // diciendo "Sin compras todavia" de un cliente que compro tres veces en la
  // tienda. Con el modulo apagado se muestra lo que ese negocio SI sabe: que
  // busca, que talla dijo, quien lo atiende.
  const modInstalaciones = crmView?.osito?.modulos?.instalaciones !== false;

  const { data, isLoading } = useQuery({
    queryKey: ['clientes', 'list', { search, compro, zona, esteMes }],
    queryFn: () =>
      clientesApi.listClientes({
        search: search.trim() || undefined,
        compro: compro === 'todos' ? undefined : compro,
        zona: zona || undefined,
        esteMes: esteMes ? 'true' : undefined,
        take: 100,
      }),
  });

  const zonaOptions = [
    { value: '', label: 'Zona: todas' },
    ...(crmView?.crm.zonas ?? []).map((z) => ({ value: z, label: z })),
  ];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Clientes"
        description={
          modInstalaciones
            ? 'Tu cartera: quién te compró, qué se llevó y cuánto ha dejado cada uno.'
            : 'Tu cartera: quién escribió, qué buscaba y quién lo está atendiendo.'
        }
      />
      {/* A2 · Filtros guardados. Lee la ruta y el ?query de la URL por su cuenta. */}
      <FiltrosGuardados />

      {/* Buscador + filtros simples */}
      <div className="flex flex-wrap items-center gap-3">
        {/* A7 · La misma caja que en Contactos, CRM y la bandeja. */}
        <CajaDeBusqueda
          valor={search}
          onChange={setSearch}
          className="w-full max-w-xs"
        />
        {/* "Compraron / No compraron" se resuelve contra trabajos completados:
            sin ese modulo, cualquiera de los dos filtros devuelve una lista
            entera o vacia, y ninguna de las dos respuestas es cierta. */}
        {modInstalaciones && (
        <div className="flex items-center gap-1 rounded-md border border-surface-border bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
          {COMPRO_CHIPS.map((chip) => (
            <button
              key={chip.value}
              onClick={() => setCompro(chip.value)}
              className={cn(
                'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                compro === chip.value
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
        )}
        <div className="w-44">
          <Select options={zonaOptions} value={zona} onChange={(e) => setZona(e.target.value)} />
        </div>
        {modInstalaciones && (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={esteMes}
            onChange={(e) => setEsteMes(e.target.checked)}
            className="h-4 w-4 rounded border-surface-border text-brand-600 focus:ring-brand-500"
          />
          Compraron este mes
        </label>
        )}
      </div>

      {isLoading ? (
        <Cargando />
      ) : !data?.items.length ? (
        <EmptyState
          icon={Users}
          title="Sin clientes con ese filtro"
          description="Prueba con otro filtro o borra la búsqueda."
        />
      ) : (
        <Card>
          <div className="flex items-center justify-between border-b border-surface-border p-5 dark:border-slate-800">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {data.total} cliente{data.total === 1 ? '' : 's'}
            </h2>
          </div>
          <CardBody className="p-0">
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {data.items.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/clientes/${c.id}`}
                    className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 sm:flex-nowrap"
                  >
                    <Avatar name={c.nombre} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                        {c.nombre}
                      </p>
                      <p className="truncate text-xs text-slate-400">
                        {[formatPhoneNumber(c.telefono), c.zona].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    {modInstalaciones ? (
                      <>
                        <div className="min-w-0 flex-1 text-sm">
                          {c.ultimaCompra ? (
                            <>
                              <p className="truncate text-slate-700 dark:text-slate-200">
                                {c.ultimaCompra.modelo ?? 'Compra sin modelo'} ·{' '}
                                {money(c.ultimaCompra.precio, currencyPrefix)}
                              </p>
                              <p className="truncate text-xs text-slate-400">
                                {dateTime(c.ultimaCompra.fecha)}
                                {c.tecnico ? ` · Técnico: ${c.tecnico}` : ''}
                              </p>
                            </>
                          ) : (
                            <p className="text-xs text-slate-400">Sin compras todavía</p>
                          )}
                        </div>
                        <div className="w-28 shrink-0 text-right">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {money(c.totalHistorico, currencyPrefix)}
                          </p>
                          <p className="text-xs text-slate-400">
                            {c.comprasCount} compra{c.comprasCount === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {c.estadoUltimoTrabajo && (
                            <Badge variant={jobStatusBadge(c.estadoUltimoTrabajo).variant}>
                              {jobStatusBadge(c.estadoUltimoTrabajo).label}
                            </Badge>
                          )}
                          {c.encuesta && (
                            <Badge variant={encuestaBadge(c.encuesta).variant}>
                              {encuestaBadge(c.encuesta).label}
                            </Badge>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Lo que un negocio sin trabajos SI tiene de cada
                            cliente: lo capturó el asistente en el chat. */}
                        <div className="min-w-0 flex-1 text-sm">
                          {c.osito?.producto ? (
                            <p className="truncate text-slate-700 dark:text-slate-200">
                              {c.osito.producto}
                            </p>
                          ) : (
                            <p className="text-xs text-slate-400">Todavía no dijo qué busca</p>
                          )}
                          <p className="truncate text-xs text-slate-400">
                            {[
                              c.osito?.talla ? `Talla que dijo: ${c.osito.talla}` : null,
                              c.osito?.vendedor ? `Atiende: ${c.osito.vendedor}` : 'Sin vendedor',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {c.osito?.tipoCliente === 'MAYOR' && <Badge variant="info">Mayorista</Badge>}
                          {c.osito?.descartadoAt && <Badge variant="warning">Descartado</Badge>}
                          {!c.osito?.descartadoAt && (c.osito?.intentos ?? 0) > 0 && (
                            <Badge variant="default">
                              {c.osito?.intentos} intento{c.osito?.intentos === 1 ? '' : 's'}
                            </Badge>
                          )}
                        </div>
                      </>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
